// Helper functions for filtering attendance data

/**
 * Checks if a student's name matches the filter
 * @param {string} studentName - The student's name
 * @param {string} filterName - The name filter to match against
 * @returns {boolean} - True if the name matches the filter
 */
const doesStudentNameMatch = (studentName, filterName) => !filterName || studentName.toLowerCase().includes(filterName.toLowerCase());
/**
 * Checks if a student's comment matches the filter
 * @param {string} studentComment - The student's comment
 * @param {string} filterComment - The comment filter to match against
 * @returns {boolean} - True if the comment matches the filter
 */
const doesStudentCommentMatch = (studentComment, filterComment) => !filterComment || studentComment.toLowerCase().includes(filterComment.toLowerCase());

/**
 * Checks if a student's client name matches the filter
 * @param {string} clientName - The student's client name
 * @param {string} filterClientName - The client name filter to match against
 * @returns {boolean} - True if the client name matches the filter
 */
const doesClientNameMatch = (clientName, filterClientName) => !filterClientName || (clientName || "").toLowerCase().includes(filterClientName.toLowerCase());

/**
 * Determines if a student should be filtered out based on filter criteria
 * @param {Object} student - The student object
 * @param {Object} filterList - The filter criteria
 * @returns {boolean} - True if the student should be filtered out
 */
const shouldFilterOutStudent = (student, filterList) => {
  // const isPresent = !!student.isPresent;
  const studentNameMatches = doesStudentNameMatch(
    student.studentName,
    filterList.studentName,
  );
  const studentCommentMatches = doesStudentCommentMatch(
    student.comment,
    filterList.comment,
  );
  const clientNameMatches = doesClientNameMatch(
    student.clientName || "",
    filterList.clientName,
  );

  const filterOut =
    // !isPresent ||
    !studentNameMatches ||
    !studentCommentMatches ||
    !clientNameMatches ||
    !student.included;

  return filterOut;
};

/**
 * Determines if a group should be filtered out based on filter criteria
 * @param {Object} group - The group object
 * @param {Object} filterList - The filter criteria
 * @returns {boolean} - True if the group should be filtered out
 */
const shouldFilterOutGroup = (group, filterList) => group.students.every(student => shouldFilterOutStudent(student, filterList));

/**
 * Filters attendance data based on filter criteria
 * @param {Array} attendanceData - Array of attendance group objects
 * @param {Object} filters - Filter criteria
 * @returns {Array} - Filtered attendance data
 */
const filterAttendanceData = (attendanceData, filters) => attendanceData.filter((reg) => {
  const isFullPresence = reg.presentCount === reg.maxMembers;
  const groupStudentNameMatches = reg.students.some(student => doesStudentNameMatch(student.studentName, filters.studentName));
  const studentCommentMatches = reg.students.some(student => doesStudentCommentMatch(student.comment, filters.comment));
  const groupClientNameMatches = reg.students.some(student => doesClientNameMatch(student.clientName || "", filters.clientName));

  return (
    (!filters.isPresent || isFullPresence) &&
    (filters.date === "" || reg.date.includes(filters.date)) &&
    (filters.dayOfWeek === "" || reg.dayOfWeek.includes(filters.dayOfWeek)) &&
    (filters.startTime === "" || reg.startTime.includes(filters.startTime)) &&
    (filters.classType === "" || reg.classType.includes(filters.classType)) &&
    (filters.teacherName === "" ||
      reg.teacherName
        .toLowerCase()
        .includes(filters.teacherName.toLowerCase())) &&
    (filters.comment === "" || studentCommentMatches) &&
    (filters.studentName === "" || groupStudentNameMatches) &&
    (filters.clientName === "" || groupClientNameMatches)
  );
});

export {
  shouldFilterOutStudent,
  shouldFilterOutGroup,
  filterAttendanceData,
};
